/**
 * Chip-ID normalization — docs/m26-mitarbeiterportal/SPEC.md §3.7: "uppercase, non-hex strippen, exakt
 * 8 Zeichen", applied identically on both the scan and typed-fallback
 * paths so the server always receives the same shape.
 */

export function normalizeChip(raw: string): string {
  return raw.toUpperCase().replace(/[^0-9A-F]/g, '')
}

export function isValidChip(chip: string): boolean {
  return /^[0-9A-F]{8}$/.test(chip)
}

/**
 * Printed QR codes on school cards open /mitarbeiter/anmelden#k=XXXXXXXX
 * (M27 T10). Returns the normalized chip for a valid fragment, else null.
 * Exactly 8 hex characters — no trimming of longer values, so a mangled
 * fragment never passes as a different chip.
 */
export function parseChipFragment(hash: string): string | null {
  const m = /^#k=([0-9a-f]{8})$/i.exec(hash)
  if (!m) return null
  const chip = normalizeChip(m[1])
  return isValidChip(chip) ? chip : null
}

/**
 * Phone pairing QR (M27 H): /mitarbeiter/anmelden#g=<code>. The code is 16
 * random bytes base64url = exactly 22 characters. Case is significant.
 */
export function parseKopplungFragment(hash: string): string | null {
  const m = /^#g=([A-Za-z0-9_-]{22})$/.exec(hash)
  return m ? m[1] : null
}

/**
 * One reader/scanner burst → chip number (M27, Astra review 05.10.2026).
 * - Chip reader (only hex, maybe a trailing checksum/terminator): the FIRST 8
 *   hex characters — unchanged rule (plan D11).
 * - USB QR scanner: the printed URL …/mitarbeiter/anmelden#k=XXXXXXXX arrives
 *   as typed text; '#' and '=' may come out garbled under a German keyboard
 *   layout. Any letter g–z marks such a URL: then the trailing run of hex
 *   characters is the chip and must be exactly 8 long. (The old rule took the
 *   first 8 hex of the URL itself — "EAACAEED" for every card.)
 */
export function chipFromBurst(raw: string): string | null {
  const s = raw.trim()
  if (/[g-z]/i.test(s)) {
    const m = /([0-9a-f]+)$/i.exec(s)
    return m && m[1].length === 8 ? m[1].toUpperCase() : null
  }
  const chip = normalizeChip(s).slice(0, 8)
  return isValidChip(chip) ? chip : null
}

/** Average gap below this = a machine typed it (reader/scanner), never a person. */
export const MACHINE_GAP_MS = 35

/** Short chip bursts finish within 300 ms; long QR bursts are judged by their average key gap. */
export function isMachineBurst(chars: number, burstMs: number, windowMs = 300): boolean {
  if (chars < 2) return false
  return burstMs < windowMs || burstMs / (chars - 1) < MACHINE_GAP_MS
}
